import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectionNames } from '@/entities/connection';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { CatalogRow } from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';
import { LoadingState, UnavailableState } from '@/shared/ui/States';
import { EmptyState } from '@/shared/ui/EmptyState';

import { useReachLookup } from '../api/lineage';
import { summaryFor, summaryKey } from '../model/presentation';
import type { LineageSummary } from '../model/presentation';

/* Pick a dataset. The whole of `/lineage` until one is picked.
 *
 * **This replaced a `<select>` of every dataset in the estate, and the replacement is the point
 * rather than the styling.** A dropdown answers "which of these do I want" only for somebody who
 * already knows the name they are looking for; a steward arriving at a lineage screen usually does
 * not — they want to see which tables *have* lineage worth looking at. So the list is grouped by
 * source, exactly as the catalogue is, and every row carries what the graph would show. The dropdown
 * also collapsed the four states below into one: a dataset with no lineage was indistinguishable
 * from one with plenty until you selected it and got an empty canvas.
 *
 * **Grouping comes from `catalog-browser` rather than being written again here.** That module's
 * `groupByConnection` already holds the four-label decision a group heading needs — named, loading,
 * unreadable, unlisted — and a second implementation is how two screens come to disagree about what
 * a missing connection name means. Same reasoning as `useCursorStack` being extracted rather than
 * copied for the second keyset listing.
 *
 * **Two requests for the whole page, not two per row.** `/v1/lineage/node-reach` exists for this: it
 * returns both counts for every node at once, so the index costs one request however many datasets a
 * source has. Getting the same numbers from `impact` and `provenance` would be an N+1 on a listing —
 * the shape this codebase declined once already for the executions table, where the reference design
 * wanted a violation total and the honest answer was to move it to the run's own page.
 *
 * Search is client-side over the page the catalogue returned, deliberately: the reach map is keyed on
 * the whole tenant's nodes and re-fetching per keystroke would spend a round trip to filter a list
 * already in memory. The bound is stated on screen — this is the first page of the catalogue, not the
 * estate — because a filter that silently searches a subset is worse than no filter.
 */

/** One page of the catalogue, which is what the picker lists. Matches `DATASETS_PER_PAGE`'s reasoning
 * in the browser: a person reads a page, and paging here would need the cursor stack the browser
 * already worked out. Stated on screen rather than implied. */
const PICKER_DATASETS = 200;

export function LineagePicker({
  onPick,
}: {
  readonly onPick: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();

  const datasets = useDatasets({ search: '', connectionId: null });
  const connectionNames = useConnectionNames();
  /* The join lives in `useReachLookup` now, because the dataset bar needs the identical answer and
     a second copy would be two answers to "does this dataset have lineage". */
  const lookup = useReachLookup();

  /* **No filter here, and the absence is the design.** `DatasetBar` above this list is a
     combobox over the same datasets that searches name *and* connection, so a second box searching
     names only was two controls answering one question with different answers — and the narrower
     one sat lower on the page, which is the one a reader reaches for last and trusts most.
     `groups.length === 0` therefore means the catalogue is empty rather than "nothing matched",
     which is why the empty state below says so. */
  const groups = useMemo(() => {
    if (!datasets.isSuccess) return [];
    return groupByConnection(datasets.data.items, {
      status: connectionNames.isPending
        ? 'loading'
        : connectionNames.isError
          ? 'unreadable'
          : 'ready',
      names: connectionNames.data ?? new Map<string, string>(),
    });
  }, [
    datasets.isSuccess,
    datasets.data,
    connectionNames.isPending,
    connectionNames.isError,
    connectionNames.data,
  ]);

  if (datasets.isPending) {
    return <LoadingState rows={5} label={t('lineage.pick.loading')} />;
  }
  if (datasets.isError) {
    return (
      <UnavailableState
        title={t('lineage.pick.unreadable')}
        guidance={datasets.error.message}
      />
    );
  }

  return (
    <div className="lq-picker">
      {/* **Said once about the tenant, rather than implied fourteen times about datasets.**
          Every row already reports its own reach honestly, and when the graph is *entirely* empty
          that produces the same sentence beside every table — which reads as a per-dataset finding,
          or as a broken screen, when the truth is a fact about the estate: nothing has ever been
          ingested.

          The distinction matters because the row-level sentence cannot make it. "No lineage
          recorded" beside one table among many is a statement about that table; the identical
          sentence beside all of them is a statement about us. Verified on a real estate whose
          fourteen tables are each loaded directly from a file with nothing derived from anything —
          the rows were correct and the page still read as a fault.

          Only when the reach read actually succeeded. A failed read already renders `unreadable`
          per row, and claiming "nothing has been ingested" on the strength of a failed request
          would be the absence-is-not-a-failure defect inverted. */}
      {lookup.status === 'ready' && lookup.byDataset.size === 0 ? (
        <Alert tone="notice" title={t('lineage.pick.graphEmptyTitle')}>
          {t('lineage.pick.graphEmptyBody')}
        </Alert>
      ) : null}

      {groups.length === 0 ? (
        /* An empty *catalogue*, which is what an empty list means now that nothing filters it.
           Reporting it as "no dataset matches that name" would be the filter-versus-absence
           confusion inverted: a claim about a search nobody ran. */
        <EmptyState
          title={t('lineage.pick.noDatasets')}
          guidance={t('lineage.pick.noDatasetsWhy')}
        />
      ) : (
        groups.map((group) => (
          <section className="lq-picker__group" key={group.connectionId}>
            <h2 className="lq-picker__heading">
              {group.label.kind === 'named'
                ? group.label.name
                : t(`catalog.group.${group.label.kind}`)}
            </h2>
            <div className="lq-picker__rows">
              {group.rows.map((row) => (
                <PickerRow
                  key={row.id}
                  row={row}
                  summary={summaryFor(row.id, lookup)}
                  onPick={onPick}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <p className="lq-picker__note">
        {t('lineage.pick.note', { limit: PICKER_DATASETS })}
      </p>
    </div>
  );
}

/* One dataset.
 *
 * **The link is offered only where there is something to open**, which is the row's whole
 * information design: a "View lineage" beside a dataset with none leads to an empty canvas, and an
 * empty canvas is indistinguishable from a broken screen. So the three non-counted states say what
 * they are instead, and the name is dimmed only for `none` — the one state that is a fact about the
 * dataset rather than about our own request.
 */
function PickerRow({
  row,
  summary,
  onPick,
}: {
  readonly row: CatalogRow;
  readonly summary: LineageSummary;
  readonly onPick: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const name = `${row.schema_name}.${row.object_name}`;

  return (
    <div className="lq-picker__row">
      <span
        className={
          summary.kind === 'none'
            ? 'lq-picker__name lq-picker__name--quiet'
            : 'lq-picker__name'
        }
      >
        {name}
      </span>
      <span className="lq-picker__count">
        {summary.kind === 'counted'
          ? t('lineage.pick.counted', {
              upstream: summary.upstream,
              downstream: summary.downstream,
            })
          : t(summaryKey(summary))}
      </span>
      {summary.kind === 'counted' ? (
        <button
          type="button"
          className="lq-link-button"
          onClick={() => {
            onPick(row.id);
          }}
        >
          {t('lineage.pick.view')}
        </button>
      ) : null}
    </div>
  );
}
